import { type PaletteOptions } from "@mui/material/styles";

// Do not export colors directly, use the palette colors instead
const colors = {
  common: {
    black: "#000000",
    white: "#FFFFFF",
  },
  anchor: {
    100: "#E5EDF8",
    200: "#B6CFF5",
    300: "#89B2F0",
    400: "#407BD2",
    500: "#004BB9",
    600: "#07469C",
    700: "#0C3F81",
    800: "#0F3767",
    900: "#002855",
  },
  harbor: {
    100: "#E9F0F5",
    200: "#CEDBE8",
    300: "#98B3CD",
    400: "#5F8CB3",
    500: "#216D9E",
    600: "#1C5D86",
    700: "#174D6F",
    800: "#123C57",
    900: "#0D2C3F",
  },
  grove: {
    100: "#EFF6F4",
    200: "#C7DDD7",
    300: "#85B3A6",
    400: "#458C78",
    500: "#1B765D",
    600: "#0E6B51",
    700: "#14604B",
    800: "#135240",
    900: "#0D3C2E",
  },
  ember: {
    100: "#FAEDEE",
    200: "#F4C9CA",
    300: "#E5848B",
    400: "#CA4A59",
    500: "#B94450",
    600: "#A23B47",
    700: "#832E38",
    800: "#68252D",
    900: "#511E24",
  },
  charcoal: {
    50: "#F6F6F6",
    100: "#EFEFEF",
    200: "#DADADA",
    300: "#AFAFAF",
    400: "#848484",
    500: "#656565",
    600: "#5B5A5A",
    700: "#4D4D4D",
    800: "#383838",
    900: "#282828",
  },
  dune: {
    50: "#F8F7F4",
    100: "#F3F1EA",
    200: "#DDDCD5",
    300: "#BBBAB4",
    400: "#8C8A81",
    500: "#6C695B",
    600: "#5B584D",
    700: "#4B493E",
    800: "#3B392F",
    900: "#2F2D25",
  },
  red: {
    100: "#FEE4E2",
    200: "#FECDCA",
    300: "#FDA29B",
    400: "#F97066",
    500: "#F04438",
    600: "#D92D20",
    700: "#B42318",
    800: "#7A271A",
    900: "#55160C",
  },
  amber: {
    100: "#FEF0C7",
    200: "#FEDF89",
    300: "#FEC84B",
    400: "#FDB022",
    500: "#F79009",
    600: "#DC6803",
    700: "#B54708",
    800: "#7A2E0E",
    900: "#4E1D09",
  },
  green: {
    100: "#DCFAE6",
    200: "#ABEFC6",
    300: "#75E0A7",
    400: "#47CD89",
    500: "#17B26A",
    600: "#079455",
    700: "#067647",
    800: "#085D3A",
    900: "#053321",
  },
  violet: {
    100: "#E8E2FE",
    200: "#D9CAFE",
    300: "#B89BFD",
    400: "#9966F9",
    500: "#8238F0",
    600: "#7320D9",
    700: "#6118B4",
    800: "#471A7A",
    900: "#370C55",
  },
} as const;

const { common, charcoal, anchor, harbor, ember, grove, dune, amber, green, red, violet } = colors;

export const editorColors = {
  warning: {
    bg: amber[500],
    border: amber[700],
    text: charcoal[500],
  },
  hint: {
    bg: anchor[500],
    border: anchor[600],
    text: common.white,
  },
  success: {
    bg: green[100],
    border: green[500],
    text: charcoal[500],
  },
  error: {
    bg: red[100],
    border: red[500],
    text: charcoal[500],
  },
} as const;

export const textColors = {
  primary: charcoal[500],
  secondary: charcoal[900],
  tertiary: charcoal[700],
  brand: anchor[500],
  disabled: charcoal[300],
  accent1: harbor[500],
  accent2: grove[500],
  accent3: ember[500],
  light: common.white,
  contrastText: common.white,
} as const;

/**
 * AD2 tokens for the /smile campaign surfaces (Dustin V2 pages, Figma
 * Gu7BpA3xPHYUBzESa6EC0n and the 158:442 proto in vy1bHbX6sIjly3EzwAQA62).
 * "Primary Blue" #0046F8 is the resolved canon over anchor[500] #004BB9,
 * the same adjudication the shipped /smile header used. Navy #012368 is
 * the AD2 heading and footer ink (Figma var "Text Dark"). Additive set:
 * nothing outside the /smile campaign consumes these.
 */
export const ad2Colors = {
  primaryBlue: "#0046F8", // Figma var "Primary Blue": pill label + arrow ink (309:2332/2333)
  primaryBlueTint: "#E7F0FC", // light tint behind the header utility strip and the scheduler overflow day chip
  navy: "#012368", // Figma var "Text Dark": section heading ink (309:2312)
  backgroundLight: "#FAFAFA", // Figma var "Background Light": section fill (309:2309)
  cardHairline: "rgba(0, 0, 0, 0.08)", // card rect border (309:2330)
  dropShadow: "0px 0px 40px 0px rgba(15, 31, 53, 0.08)", // Figma effect "Primary Drop Shadow" (#0F1F3514 r40), apply once per card
  familyNavy: "#002855", // Figma 318:3837 TAG family strip fill (== anchor[900])
  textMedium: "#515152", // AD2 secondary body ink (Figma "Text Medium")
  textBlack: "#1A1A1A", // AD2 near-black body ink (Figma "Text Black")
  bandSand: "#F3F1EA", // AD2 sand band fill (== dune[100]) for logo strips and alt bands
} as const;

export const backgrounds = {
  white: common.white,
  neutral2Lightest: dune[50],
  neutral2Light: dune[100],
  primaryLightest: anchor[100],
  accent1Lightest: harbor[100],
  accent2Lightest: grove[100],
  accent3Lightest: ember[100],
  primaryMain: anchor[500],
  accent1Main: harbor[500],
  accent2Main: grove[500],
  accent3Main: ember[500],
  primaryDarkest: anchor[900],
  accent1Darkest: harbor[900],
  accent2Darkest: grove[900],
  accent3Darkest: ember[900],
  paper: common.white,
} as const;

const borderColors = {
  neutral1Light: charcoal[200],
  neutral1Medium: charcoal[300],
  neutral1Primary: charcoal[500],
};

const iconColors = {
  contrast: common.white,
  light: common.white,
  primary: charcoal[500],
  secondary: charcoal[900],
  tertiary: charcoal[700],
  brand: anchor[500],
  accent1: harbor[500],
  accent2: grove[500],
  accent3: ember[500],
  disabled: charcoal[300],
};

const gradients = {
  primary: `linear-gradient(307deg, ${anchor[700]} 27.13%, ${anchor[500]} 72.87%)`,
  primaryDark: `linear-gradient(307deg, ${anchor[900]} 27.13%, ${anchor[700]} 72.87%)`,
  accent1: `linear-gradient(307deg, ${harbor[700]} 27.13%, ${harbor[500]} 72.87%)`,
  accent2: `linear-gradient(307deg, ${grove[700]} 27.13%, ${grove[500]} 72.87%)`,
  accent3: `linear-gradient(307deg, ${ember[700]} 27.13%, ${ember[500]} 72.87%)`,
};

export const badgeColors = {
  brandBold: anchor[700],
  brandMuted: anchor[100],
  neutralBold: common.white,
  neutralMuted: dune[100],
  accent1Bold: harbor[700],
  accent1Muted: harbor[100],
  accent2Bold: grove[700],
  accent2Muted: grove[100],
  accent3Bold: ember[700],
  accent3Muted: ember[100],
  successBold: green[700],
  successMuted: green[100],
  warningBold: amber[700],
  warningMuted: amber[100],
  errorBold: red[700],
  errorMuted: red[100],
  referenceBold: violet[700],
  referenceMuted: violet[100],
  infoBold: anchor[700],
  infoMuted: anchor[100],
} as const;

export const palette: PaletteOptions = {
  mode: "light",
  common,
  text: textColors,
  backgrounds,
  borders: borderColors,
  icons: iconColors,
  gradients,
  primary: {
    lightest: anchor[100],
    light: anchor[200],
    main: anchor[500],
    dark: anchor[700],
    darkest: anchor[900],
  },
  accent1: {
    lightest: harbor[100],
    light: harbor[200],
    main: harbor[500],
    dark: harbor[700],
    darkest: harbor[900],
  },
  accent2: {
    lightest: grove[100],
    light: grove[200],
    main: grove[500],
    dark: grove[700],
    darkest: grove[900],
  },
  accent3: {
    lightest: ember[100],
    light: ember[200],
    main: ember[500],
    dark: ember[700],
    darkest: ember[900],
  },
  neutral1: {
    lightest: charcoal[50],
    light: charcoal[100],
    medium: charcoal[300],
    main: charcoal[500],
    dark: charcoal[700],
    darkest: charcoal[900],
  },
  neutral2: {
    lightest: dune[50],
    light: dune[100],
    medium: dune[200],
  },
  error: {
    lightest: colors.red[100],
    main: colors.red[500],
    dark: colors.red[700],
    darkest: colors.red[900],
  },
  warning: {
    lightest: colors.amber[100],
    main: colors.amber[500],
    dark: colors.amber[700],
    darkest: colors.amber[900],
  },
  success: {
    lightest: colors.green[100],
    main: colors.green[500],
    dark: colors.green[700],
    darkest: colors.green[900],
  },
  info: {
    lightest: anchor[100],
    main: anchor[500],
    dark: anchor[700],
    darkest: anchor[900],
  },
  reference: {
    lightest: colors.violet[100],
    main: colors.violet[500],
    dark: colors.violet[700],
    darkest: colors.violet[900],
  },
  components: {
    rating: colors.amber[400],
  },
};

/**
 * Palette for the /smile campaign routes ONLY, selected in CommonLayout via
 * isSmileCampaignRoute (see ./index.ts smileCampaignTheme).
 *
 * TAG owns the global styling on aspendental.com and BB cannot change it. The
 * /smile namespace is the sandbox TAG designated for BB, and this palette is
 * how colour is controlled there: swap the theme, never a global rule and
 * never a per-component hex. Values come from `ad2Colors` above, which already
 * carried the resolved Figma canon but was not wired into any theme.
 *
 * Only the slots the AD2 design actually redefines are overridden. Everything
 * else falls through to the shared palette, so a token that has no AD2 opinion
 * keeps one definition in one place.
 */
/**
 * Named consts, not inline literals: the augmented `TypeText` / `Backgrounds`
 * interfaces do not declare every key this palette carries (`brand`,
 * `neutral2Lightest`), and an inline object literal would trip excess-property
 * checks. The base palette above assigns `textColors` / `backgrounds` the same
 * way for the same reason.
 */
const smileTextColors = {
  ...textColors,
  // AD2 inks (Figma "Text Black" / "Text Dark" / "Text Medium").
  primary: ad2Colors.textBlack,
  secondary: ad2Colors.navy,
  tertiary: ad2Colors.textMedium,
  brand: ad2Colors.primaryBlue,
} as const;

const smileBackgrounds = {
  ...backgrounds,
  neutral2Lightest: ad2Colors.backgroundLight,
  neutral2Light: ad2Colors.bandSand,
  primaryDarkest: ad2Colors.familyNavy,
} as const;

export const smilePalette: PaletteOptions = {
  ...palette,
  text: smileTextColors,
  backgrounds: smileBackgrounds,
  primary: {
    ...(palette.primary as Record<string, string>),
    // "Primary Blue" #0046F8 is the resolved canon over anchor[500] #004BB9,
    // the same adjudication the shipped /smile header already made by hand.
    main: ad2Colors.primaryBlue,
    darkest: ad2Colors.navy,
  },
};
